/**
 * KYC Service - API client for KYC operations
 * Handles all communication with the KYC backend service
 */

import axios from 'axios';
import { KYCFormData } from '../types/kyc.types';
import { API_CONFIG } from '../config/api-config';

// Country name to ISO code mapping (supports multiple languages and ISO 3166-1 alpha-3)
const countryToISO: Record<string, string> = {
  // Switzerland (multiple languages + nationality)
  'Swiss': 'CH',
  'Switzerland': 'CH',
  'Schweiz': 'CH',  // German
  'Suisse': 'CH',   // French
  'Svizzera': 'CH', // Italian
  'CHE': 'CH',      // ISO 3166-1 alpha-3

  // Common countries (including nationality adjectives)
  'United States': 'US',
  'USA': 'US',
  'American': 'US',     // Nationality adjective
  'United Kingdom': 'GB',
  'UK': 'GB',
  'British': 'GB',      // Nationality adjective
  'Germany': 'DE',
  'Deutschland': 'DE',  // German
  'German': 'DE',       // Nationality adjective
  'DEU': 'DE',          // ISO 3166-1 alpha-3
  'France': 'FR',
  'French': 'FR',       // Nationality adjective
  'FRA': 'FR',
  'Italy': 'IT',
  'Italia': 'IT',       // Italian
  'Italian': 'IT',      // Nationality adjective
  'ITA': 'IT',
  'Spain': 'ES',
  'España': 'ES',       // Spanish
  'Spanish': 'ES',      // Nationality adjective
  'ESP': 'ES',
  'Netherlands': 'NL',
  'Nederland': 'NL',    // Dutch
  'NLD': 'NL',
  'Belgium': 'BE',
  'Belgique': 'BE',     // French
  'België': 'BE',       // Dutch
  'BEL': 'BE',
  'Austria': 'AT',
  'Österreich': 'AT',   // German
  'AUT': 'AT',
  'Portugal': 'PT',
  'PRT': 'PT',
  'Sweden': 'SE',
  'Sverige': 'SE',      // Swedish
  'SWE': 'SE',
  'Norway': 'NO',
  'Norge': 'NO',        // Norwegian
  'NOR': 'NO',
  'Denmark': 'DK',
  'Danmark': 'DK',      // Danish
  'DNK': 'DK',
  'Finland': 'FI',
  'Suomi': 'FI',        // Finnish
  'FIN': 'FI',
  'Ireland': 'IE',
  'IRL': 'IE',
  'Luxembourg': 'LU',
  'LUX': 'LU',
  'Canada': 'CA',
  'CAN': 'CA',
  'Australia': 'AU',
  'AUS': 'AU',
  'Japan': 'JP',
  'JPN': 'JP',
  'Singapore': 'SG',
  'SGP': 'SG',
  'Hong Kong': 'HK',
  'HKG': 'HK',
  'China': 'CN',
  'CHN': 'CN',
  'India': 'IN',
  'IND': 'IN',
  'Brazil': 'BR',
  'Brasil': 'BR',       // Portuguese
  'Brazilian': 'BR',    // Nationality adjective
  'BRA': 'BR',          // ISO 3166-1 alpha-3
  'Mexico': 'MX',
  'México': 'MX',       // Spanish
  'MEX': 'MX',
  'South Africa': 'ZA',
  'ZAF': 'ZA',
  'Russia': 'RU',
  'Россия': 'RU',       // Russian (Cyrillic)
  'RUS': 'RU',
  'Turkey': 'TR',
  'Türkiye': 'TR',      // Turkish
  'TUR': 'TR',
  'Saudi Arabia': 'SA',
  'SAU': 'SA',
  'United Arab Emirates': 'AE',
  'UAE': 'AE',
  'ARE': 'AE',
  'Israel': 'IL',
  'ISR': 'IL',
  'South Korea': 'KR',
  'Korea': 'KR',
  'KOR': 'KR',
  'Argentina': 'AR',
  'ARG': 'AR',
  'Chile': 'CL',
  'CHL': 'CL',
  'Colombia': 'CO',
  'COL': 'CO',
  'Peru': 'PE',
  'Perú': 'PE',
  'PER': 'PE',
  'New Zealand': 'NZ',
  'NZL': 'NZ',
  'Egypt': 'EG',
  'EGY': 'EG',
  'Morocco': 'MA',
  'MAR': 'MA',
  'Nigeria': 'NG',
  'NGA': 'NG',
  'Kenya': 'KE',
  'KEN': 'KE',
  'Thailand': 'TH',
  'THA': 'TH',
  'Malaysia': 'MY',
  'MYS': 'MY',
  'Philippines': 'PH',
  'PHL': 'PH',
  'Indonesia': 'ID',
  'IDN': 'ID',
  'Vietnam': 'VN',
  'VNM': 'VN',
  'Pakistan': 'PK',
  'PAK': 'PK',
  'Bangladesh': 'BD',
  'BGD': 'BD'
};

/**
 * Remove empty strings from object, converting them to undefined
 * This helps with backend validation that expects null/undefined for optional fields
 */
const cleanEmptyStrings = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return obj === '' ? undefined : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(cleanEmptyStrings);
  }

  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const cleanedValue = cleanEmptyStrings(obj[key]);
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
        }
      }
    }
    return cleaned;
  }

  return obj;
};

/**
 * Convert country name to ISO code
 */
const toISOCode = (country: string): string => {
  // If already an ISO code (2 uppercase letters), return as is
  if (/^[A-Z]{2}$/.test(country)) {
    return country;
  }

  // Try to find in mapping (case-insensitive)
  const normalizedCountry = Object.keys(countryToISO).find(
    key => key.toLowerCase() === country.toLowerCase()
  );

  if (normalizedCountry) {
    return countryToISO[normalizedCountry];
  }

  // If not found, return the original value (will be caught by backend validation)
  console.warn(`Unknown country: ${country}`);
  return country;
};

/**
 * Sanitize phone number to E.164 format (remove spaces, dashes, parentheses)
 * Backend expects: ^\+?[1-9]\d{1,14}$ (optional +, then digits only)
 */
const sanitizePhoneNumber = (phone: string): string => {
  if (!phone) return phone;

  // Remove all non-digit characters except leading +
  const hasPlus = phone.trim().startsWith('+');
  const digitsOnly = phone.replace(/\D/g, '');

  return hasPlus ? `+${digitsOnly}` : digitsOnly;
};

/**
 * Transform wealth sources form data to backend structure
 * Maps frontend field names to backend field names
 */
/** Normalize partial dates (e.g. "2015", "2015-03") to full ISO YYYY-MM-DD */
const normalizeDate = (val: any): string | undefined => {
  if (!val) return undefined;
  const s = String(val).trim();
  if (/^\d{4}$/.test(s)) return `${s}-01-01`;
  if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return undefined;
};

const transformWealthSources = (wealthData: any): any[] => {
  if (!wealthData) return [];

  // Handle both array format and wrapper object format
  const sources = Array.isArray(wealthData) ? wealthData : (wealthData.sources || []);

  console.log('[KYC Service] transformWealthSources - Raw input:', wealthData);
  console.log('[KYC Service] transformWealthSources - Extracted sources:', sources);

  const transformed = sources.map((source: any, index: number) => {
    console.log(`[KYC Service] Transforming source #${index}:`, {
      description: source.description,
      sourceDescription: source.sourceDescription,
      source_description: source.source_description,
      sourceType: source.sourceType,
      percentage: source.percentage
    });

    return {
      sourceType: source.sourceType || source.source_type,
      sourceDescription: source.description || source.sourceDescription || source.source_description,  // FIX: Map description to sourceDescription
      percentage: source.percentage,
      acquisitionPeriod: source.acquisitionPeriod || source.acquisition_period,
      acquisitionStartDate: normalizeDate(source.acquisitionStartDate || source.acquisition_start_date),
      acquisitionEndDate: normalizeDate(source.acquisitionEndDate || source.acquisition_end_date),
      isOngoing: source.isOngoing ?? source.is_ongoing ?? false,
      supportingDocumentTypes: source.supportingDocumentTypes || source.supporting_document_types || [],
      uploadedDocumentIds: source.uploadedDocumentIds || source.uploaded_document_ids || [],
      assetType: source.assetType || source.asset_type,
      assetValue: source.amount || source.assetValue || source.asset_value,  // Map amount to assetValue
      assetCurrency: source.assetCurrency || source.asset_currency,
      assetLocation: source.assetLocation || source.asset_location,
      liquidityLevel: source.liquidityLevel || source.liquidity_level,
      documented: source.documented ?? false,
      verificationNotes: source.verificationNotes || source.verification_notes
    };
  });

  console.log('[KYC Service] transformWealthSources - Output:', transformed);
  return transformed;
};

/**
 * Transform adverseMedia form data (combined PEP + Adverse Media)
 * into separate pep_status and adverse_media structures for backend
 */
const transformAdverseMediaData = (adverseMedia: any): { pep_status: any; adverse_media: any[] } => {
  console.log('[KYC Service] transformAdverseMediaData - Input:', adverseMedia);

  if (!adverseMedia) {
    return { pep_status: {}, adverse_media: [] };
  }

  // Extract positions (FIXED: includes pepPosition)
  const positions: string[] = [];

  // FIX 1: Add main PEP position first
  if (adverseMedia.pepPosition) {
    positions.push(adverseMedia.pepPosition);
  }

  // Then add board positions
  if (adverseMedia.boardPositions && Array.isArray(adverseMedia.boardPositions)) {
    adverseMedia.boardPositions.forEach((bp: any) => {
      if (bp.position && !positions.includes(bp.position)) {
        positions.push(bp.position);
      }
    });
  }

  // Extract board position ISINs
  const boardPositionIsins: string[] = [];
  if (adverseMedia.boardPositions && Array.isArray(adverseMedia.boardPositions)) {
    adverseMedia.boardPositions.forEach((bp: any) => {
      if (bp.isin) boardPositionIsins.push(bp.isin);
    });
  }

  // Extract countries (FIXED: handles pepCountry)
  const countries: string[] = [];

  // FIX 2: Add PEP country
  if (adverseMedia.pepCountry) {
    countries.push(adverseMedia.pepCountry);
  }

  // Add board position countries (avoid duplicates)
  if (adverseMedia.boardPositions && Array.isArray(adverseMedia.boardPositions)) {
    adverseMedia.boardPositions.forEach((bp: any) => {
      if (bp.country && !countries.includes(bp.country)) {
        countries.push(bp.country);
      }
    });
  }

  // Determine classification based on PEP/RCA status
  const isPEP = adverseMedia.isPEP === true;
  const isRCA = adverseMedia.isRCA === true;
  let classification = 'not_pep';
  if (isPEP) {
    classification = 'pep';
  } else if (isRCA) {
    classification = 'rca';
  }

  console.log('[KYC Service] PEP Status:', { isPEP, isRCA, classification, positions, countries });

  // Build PEP Status object
  const pep_status: any = {
    classification,
    isPEP,
    isRCA,
    pepType: adverseMedia.pepType || undefined,
    positions: positions.length > 0 ? positions : [],
    countries: countries.length > 0 ? countries : [],
    boardPositionIsins: boardPositionIsins.length > 0 ? boardPositionIsins : [],
    startDate: adverseMedia.pepStartDate || undefined,
    endDate: adverseMedia.pepEndDate || undefined,
    relatedPeps: isRCA && adverseMedia.rcaPEPName ? [{
      name: adverseMedia.rcaPEPName,
      relationship: adverseMedia.rcaRelationship || ''
    }] : [],
    relationshipType: adverseMedia.rcaRelationship || undefined,
    pepRiskLevel: adverseMedia.reputationalRisk || undefined,
    enhancedDueDiligence: adverseMedia.reputationalRisk === 'high' || adverseMedia.reputationalRisk === 'critical',
    monitoringFrequency: isPEP ? 'monthly' : undefined
  };

  // FIX 3: Build adverse media findings with sanctions data
  const findings: any[] = [];

  // Add adverse media details
  if (adverseMedia.adverseMediaDetails) {
    findings.push({
      type: 'adverse_media',
      summary: adverseMedia.adverseMediaDetails,
      categories: adverseMedia.adverseMediaType || []
    });
  }

  // Add sanctions details
  if (adverseMedia.sanctionsHit && adverseMedia.sanctionsDetails) {
    findings.push({
      type: 'sanctions',
      summary: adverseMedia.sanctionsDetails,
      categories: ['sanctions']
    });
  }

  // Add insider flag to findings if present
  if (adverseMedia.insiderFlag) {
    findings.push({
      type: 'insider_position',
      summary: 'Client holds insider positions in publicly traded companies',
      categories: ['insider'],
      metadata: {
        boardPositions: adverseMedia.boardPositions || []
      }
    });
  }

  // Build Adverse Media array (one screening record)
  const adverse_media: any[] = [{
    screeningDate: adverseMedia.screeningDate || new Date().toISOString(),
    screeningTool: adverseMedia.sanctionsScreened ? 'Manual + Sanctions Lists' : 'Manual',
    hasAdverseMedia: adverseMedia.hasAdverseMedia || adverseMedia.sanctionsHit || false,
    severityLevel: (adverseMedia.hasAdverseMedia || adverseMedia.sanctionsHit)
      ? adverseMedia.reputationalRisk
      : undefined,
    findings: findings.length > 0 ? findings : [],
    categories: [
      ...(adverseMedia.adverseMediaType || []),
      ...(adverseMedia.sanctionsHit ? ['sanctions'] : []),
      ...(adverseMedia.insiderFlag ? ['insider'] : [])
    ].filter((v, i, a) => a.indexOf(v) === i), // Remove duplicates
    reviewed: true,
    reviewedBy: 'client',
    falsePositive: false
  }];

  const result = { pep_status, adverse_media };
  console.log('[KYC Service] transformAdverseMediaData - Output:', result);
  return result;
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and account ID if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add account ID header for RLS
  const accountId = localStorage.getItem('account_id');
  if (accountId) {
    config.headers['X-Account-ID'] = accountId;
  }

  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login if needed
      console.error('Unauthorized access');
    }
    return Promise.reject(error);
  }
);

/**
 * KYC Profile Operations
 */
export const kycProfileAPI = {
  /**
   * Create a new KYC profile
   */
  create: async (profileData: Partial<KYCFormData>) => {
    // Transform frontend data structure to match backend expectations
    const rawPayload = {
      client_id: profileData.clientId,
      personal_info: {
        first_name: profileData.personalInfo?.firstName,
        middle_name: profileData.personalInfo?.middleName,
        last_name: profileData.personalInfo?.lastName,
        passport_number: profileData.personalInfo?.passportNumber,
        national_id: profileData.personalInfo?.nationalId,
        ssn: profileData.personalInfo?.ssn,
        date_of_birth: profileData.personalInfo?.dateOfBirth,
        place_of_birth: profileData.personalInfo?.placeOfBirth,
        gender: profileData.personalInfo?.gender,
        marital_status: profileData.personalInfo?.maritalStatus,
        nationalities: (profileData.personalInfo?.nationalities || []).map(toISOCode),
        former_names: profileData.personalInfo?.formerNames || [],
        languages: profileData.personalInfo?.languages || []
      },
      contact_info: {
        phone_primary: profileData.contactInfo?.phonePrimary ?
          sanitizePhoneNumber(profileData.contactInfo.phonePrimary) : undefined,
        phone_secondary: profileData.contactInfo?.phoneSecondary ?
          sanitizePhoneNumber(profileData.contactInfo.phoneSecondary) : undefined,
        email_primary: profileData.contactInfo?.emailPrimary,
        email_secondary: profileData.contactInfo?.emailSecondary,
        preferred_contact_method: profileData.contactInfo?.preferredContactMethod,
        relationship_reason: profileData.contactInfo?.relationshipReason,
        relationship_purpose: profileData.contactInfo?.relationshipPurpose,
        previous_addresses: profileData.contactInfo?.previousAddresses || []
      },
      residential_address: {
        street_address: profileData.contactInfo?.residentialAddress?.street,
        street_address_2: profileData.contactInfo?.residentialAddress?.apartment,
        city: profileData.contactInfo?.residentialAddress?.city,
        state_province: profileData.contactInfo?.residentialAddress?.state,
        postal_code: profileData.contactInfo?.residentialAddress?.postalCode,
        country: profileData.contactInfo?.residentialAddress?.country ?
          toISOCode(profileData.contactInfo.residentialAddress.country) : undefined
      },
      mailing_address: (profileData.contactInfo?.mailingAddress?.street &&
                       profileData.contactInfo?.mailingAddress?.city &&
                       profileData.contactInfo?.mailingAddress?.postalCode &&
                       profileData.contactInfo?.mailingAddress?.country) ? {
        street_address: profileData.contactInfo.mailingAddress.street,
        street_address_2: profileData.contactInfo.mailingAddress.apartment || undefined,
        city: profileData.contactInfo.mailingAddress.city,
        state_province: profileData.contactInfo.mailingAddress.state || undefined,
        postal_code: profileData.contactInfo.mailingAddress.postalCode,
        country: toISOCode(profileData.contactInfo.mailingAddress.country)
      } : undefined,
      employment_info: profileData.employmentInfo ? {
        employment_status: profileData.employmentInfo.employmentStatus === 'selfEmployed'
          ? 'self-employed' : profileData.employmentInfo.employmentStatus,
        employer_name: profileData.employmentInfo.employerName,
        occupation: profileData.employmentInfo.occupation,
        employment_industry: profileData.employmentInfo.employmentIndustry,
        years_employed: profileData.employmentInfo.yearsEmployed,
        // Work address (NEW)
        work_address: (profileData.employmentInfo.workAddress?.street &&
                      profileData.employmentInfo.workAddress?.city &&
                      profileData.employmentInfo.workAddress?.country) ? {
          street_address: profileData.employmentInfo.workAddress.street,
          city: profileData.employmentInfo.workAddress.city,
          postal_code: profileData.employmentInfo.workAddress.postalCode || undefined,
          country: toISOCode(profileData.employmentInfo.workAddress.country)
        } : undefined,
        // Income information (NEW)
        annual_income: profileData.employmentInfo.annualIncome || undefined,
        income_currency: profileData.employmentInfo.incomeCurrency || undefined
      } : undefined,
      tax_info: profileData.taxInfo ? {
        tax_id: profileData.taxInfo.taxId,
        tax_residence: profileData.taxInfo.taxResidence ?
          toISOCode(profileData.taxInfo.taxResidence) : undefined,
        us_person: profileData.taxInfo.usPerson,
        fatca_status: profileData.taxInfo.fatcaStatus
      } : undefined,
      personal_background: profileData.personalBackground || undefined,
      // Transform combined adverseMedia form data into separate pep_status and adverse_media
      ...transformAdverseMediaData(profileData.adverseMedia),
      // Extract businesses array, sanitize fields for backend validation
      business_activities: (() => {
        const raw = Array.isArray(profileData.businessActivities) ? profileData.businessActivities : [];
        if (!raw.length) return undefined;
        return raw.map((ba: any) => ({
          ...ba,
          registrationCountry: /^[A-Z]{2}$/.test(ba.registrationCountry) ? ba.registrationCountry : undefined,
          ownershipPercentage: typeof ba.ownershipPercentage === 'number' ? ba.ownershipPercentage : (parseFloat(ba.ownershipPercentage) || undefined),
          annualRevenue: typeof ba.annualRevenue === 'number' ? ba.annualRevenue : (parseFloat(ba.annualRevenue) || undefined),
        }));
      })(),

      // Wealth Sources - transform to ensure correct field mapping
      wealth_sources: transformWealthSources(profileData.wealthSources),

      // Wealth Profile-Level Fields (Story 2.2.7)
      wealth_narrative: profileData.wealthNarrative || undefined,
      total_net_worth: profileData.totalNetWorth || undefined,
      liquid_net_worth: profileData.liquidNetWorth || undefined,
      net_worth_currency: profileData.netWorthCurrency || undefined,
      primary_wealth_source: Array.isArray(profileData.primaryWealthSource)
        ? profileData.primaryWealthSource.join(', ')
        : profileData.primaryWealthSource || undefined,
      asset_breakdown_cash: profileData.assetBreakdownCash || undefined,
      asset_breakdown_securities: profileData.assetBreakdownSecurities || undefined,
      asset_breakdown_real_estate: profileData.assetBreakdownRealEstate || undefined,
      asset_breakdown_business_equity: profileData.assetBreakdownBusinessEquity || undefined,
      asset_breakdown_commodities: profileData.assetBreakdownCommodities || undefined,
      asset_breakdown_crypto: profileData.assetBreakdownCrypto || undefined,
      asset_breakdown_other: profileData.assetBreakdownOther || undefined,
      inheritance_expected: profileData.inheritanceExpected || undefined,
      inheritance_details: profileData.inheritanceDetails || undefined,
      unusual_transactions: profileData.unusualTransactions || undefined,
      unusual_transaction_details: profileData.unusualTransactionDetails || undefined,
      source_documentation: profileData.sourceDocumentation || undefined,

      // Income Expectations (Story 2.2.8) - Profile Level
      expected_income_change: profileData.expectedIncomeChange || undefined,
      expected_change_details: profileData.expectedChangeDetails || undefined,
      retirement_income: profileData.retirementIncome || undefined,
      retirement_income_details: profileData.retirementIncomeDetails || undefined,
      other_financial_support: profileData.otherFinancialSupport || undefined,
      other_support_details: profileData.otherSupportDetails || undefined,

      // Income Sources - extract ONLY the sources array, dedup by (incomeType + sourceName)
      income_sources: (() => {
        const raw = profileData.incomeSources
          ? (Array.isArray(profileData.incomeSources)
              ? profileData.incomeSources
              : (profileData.incomeSources as any)?.sources || [])
          : [];
        if (!raw.length) return undefined;
        const seen = new Set<string>();
        const ISO_COUNTRY = /^[A-Z]{2}$/;
        return raw.filter((s: any) => {
          const key = `${s.incomeType}||${s.sourceName}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }).map((s: any) => ({
          ...s,
          sourceCountry: s.sourceCountry && ISO_COUNTRY.test(s.sourceCountry) ? s.sourceCountry : undefined,
        }));
      })(),

      documents: profileData.documents || undefined
    };

    // Clean empty strings from the payload
    const payload = cleanEmptyStrings(rawPayload);

    // Check for incomplete fields (warnings only - not blocking)
    const missingFields: string[] = [];

    if (!payload.client_id) {
      // Client ID is truly required - this is a blocking error
      throw new Error('Client ID is required');
    }

    // Collect missing fields for informational purposes (non-blocking)
    if (!payload.personal_info) {
      missingFields.push('Personal information');
    } else {
      if (!payload.personal_info.first_name) missingFields.push('First name');
      if (!payload.personal_info.last_name) missingFields.push('Last name');
      if (!payload.personal_info.date_of_birth) missingFields.push('Date of birth');
      if (!payload.personal_info.nationalities || payload.personal_info.nationalities.length === 0) {
        missingFields.push('Nationality');
      }
    }

    if (!payload.contact_info) {
      missingFields.push('Contact information');
    } else {
      if (!payload.contact_info.phone_primary) missingFields.push('Primary phone');
      if (!payload.contact_info.email_primary) missingFields.push('Primary email');
    }

    if (!payload.residential_address) {
      missingFields.push('Residential address');
    } else {
      if (!payload.residential_address.street_address) missingFields.push('Street address');
      if (!payload.residential_address.city) missingFields.push('City');
      if (!payload.residential_address.postal_code) missingFields.push('Postal code');
      if (!payload.residential_address.country) missingFields.push('Country');
    }

    // Log missing fields as warnings (not errors) - form can still be saved
    if (missingFields.length > 0) {
      console.warn('[KYC] Missing fields (non-blocking):', missingFields);
    }

    // Log the payload for debugging
    console.log('Sending KYC profile payload:', JSON.stringify(payload, null, 2));

    try {
      const response = await apiClient.post('/api/kyc/profiles/', payload);
      return response.data;
    } catch (error: any) {
      // Log validation errors for debugging
      if (error.response?.status === 422 && error.response?.data) {
        console.error('Backend validation errors:', error.response.data);
        console.error('Detailed validation errors:', JSON.stringify(error.response.data, null, 2));
        if (error.response.data.detail) {
          console.error('Error details:', error.response.data.detail);
          // Log each validation error separately
          error.response.data.detail.forEach((err: any, index: number) => {
            console.error(`Validation error ${index + 1}:`, {
              location: err.loc,
              message: err.msg,
              type: err.type,
              input: err.input
            });
          });
        }
        console.error('Payload that failed validation:', JSON.stringify(payload, null, 2));
      }

      // Log 500 errors with full response
      if (error.response?.status === 500) {
        console.error('Backend 500 Internal Server Error:');
        console.error('Response data:', error.response.data);
        console.error('Response text:', JSON.stringify(error.response.data, null, 2));
        console.error('Payload that caused 500:', JSON.stringify(payload, null, 2));
      }

      throw error;
    }
  },

  /**
   * Get KYC profile by client ID.
   * TASK-652: expected absence is a first-class 200 {status:'not_created'}
   * (no more console-logged 404s) — normalized to null for all consumers.
   */
  get: async (clientId: string) => {
    const response = await apiClient.get(`/api/kyc/profiles/${clientId}`);
    if (response.data?.status === 'not_created') return null;
    return response.data;
  },

  /**
   * Update existing KYC profile
   */
  update: async (clientId: string, updates: Partial<KYCFormData>) => {
    // Transform updates to backend format
    const payload = {
      personal_info: updates.personalInfo ? {
        first_name: updates.personalInfo.firstName,
        middle_name: updates.personalInfo.middleName,
        last_name: updates.personalInfo.lastName,
        date_of_birth: updates.personalInfo.dateOfBirth,
        place_of_birth: updates.personalInfo.placeOfBirth,
        gender: updates.personalInfo.gender,
        marital_status: updates.personalInfo.maritalStatus,
        nationalities: updates.personalInfo.nationalities?.map(toISOCode),
        former_names: updates.personalInfo.formerNames,
        languages: updates.personalInfo.languages
      } : undefined,
      contact_info: updates.contactInfo ? {
        phone_primary: updates.contactInfo.phonePrimary ?
          sanitizePhoneNumber(updates.contactInfo.phonePrimary) : undefined,
        phone_secondary: updates.contactInfo.phoneSecondary ?
          sanitizePhoneNumber(updates.contactInfo.phoneSecondary) : undefined,
        email_primary: updates.contactInfo.emailPrimary,
        email_secondary: updates.contactInfo.emailSecondary,
        preferred_contact_method: updates.contactInfo.preferredContactMethod,
        relationship_reason: updates.contactInfo.relationshipReason,
        relationship_purpose: updates.contactInfo.relationshipPurpose,
        previous_addresses: updates.contactInfo.previousAddresses
      } : undefined,
      residential_address: updates.contactInfo?.residentialAddress ? {
        street_address: updates.contactInfo.residentialAddress.street || '',
        street_address_2: updates.contactInfo.residentialAddress.apartment || '',
        city: updates.contactInfo.residentialAddress.city || '',
        state_province: updates.contactInfo.residentialAddress.state || '',
        postal_code: updates.contactInfo.residentialAddress.postalCode || '',
        country: toISOCode(updates.contactInfo.residentialAddress.country || '')
      } : undefined,
      mailing_address: (updates.contactInfo?.mailingAddress?.street &&
                       updates.contactInfo?.mailingAddress?.city &&
                       updates.contactInfo?.mailingAddress?.postalCode &&
                       updates.contactInfo?.mailingAddress?.country) ? {
        street_address: updates.contactInfo.mailingAddress.street,
        street_address_2: updates.contactInfo.mailingAddress.apartment || undefined,
        city: updates.contactInfo.mailingAddress.city,
        state_province: updates.contactInfo.mailingAddress.state || undefined,
        postal_code: updates.contactInfo.mailingAddress.postalCode,
        country: toISOCode(updates.contactInfo.mailingAddress.country)
      } : undefined,
      employment_info: updates.employmentInfo ? {
        employment_status: updates.employmentInfo.employmentStatus === 'selfEmployed'
          ? 'self-employed' : updates.employmentInfo.employmentStatus,
        employer_name: updates.employmentInfo.employerName,
        occupation: updates.employmentInfo.occupation,
        employment_industry: updates.employmentInfo.employmentIndustry,
        years_employed: updates.employmentInfo.yearsEmployed,
        // Work address (NEW)
        work_address: (updates.employmentInfo.workAddress?.street &&
                      updates.employmentInfo.workAddress?.city &&
                      updates.employmentInfo.workAddress?.country) ? {
          street_address: updates.employmentInfo.workAddress.street,
          city: updates.employmentInfo.workAddress.city,
          postal_code: updates.employmentInfo.workAddress.postalCode || undefined,
          country: toISOCode(updates.employmentInfo.workAddress.country)
        } : undefined,
        // Income information (NEW)
        annual_income: updates.employmentInfo.annualIncome || undefined,
        income_currency: updates.employmentInfo.incomeCurrency || undefined
      } : undefined,
      tax_info: updates.taxInfo ? {
        tax_id: updates.taxInfo.taxId,
        tax_residence: toISOCode(updates.taxInfo.taxResidence || ''),
        us_person: updates.taxInfo.usPerson,
        fatca_status: updates.taxInfo.fatcaStatus
      } : undefined,
      personal_background: updates.personalBackground || undefined,
      // Transform combined adverseMedia form data into separate pep_status and adverse_media
      ...transformAdverseMediaData(updates.adverseMedia),
      // Extract businesses array, sanitize fields for backend validation
      business_activities: (() => {
        const raw = (updates.businessActivities as any)?.businesses ||
          (Array.isArray(updates.businessActivities) ? updates.businessActivities : []);
        if (!raw?.length) return undefined;
        return raw.map((ba: any) => ({
          ...ba,
          registrationCountry: /^[A-Z]{2}$/.test(ba.registrationCountry) ? ba.registrationCountry : undefined,
          ownershipPercentage: typeof ba.ownershipPercentage === 'number' ? ba.ownershipPercentage : (parseFloat(ba.ownershipPercentage) || undefined),
          annualRevenue: typeof ba.annualRevenue === 'number' ? ba.annualRevenue : (parseFloat(ba.annualRevenue) || undefined),
        }));
      })(),

      // Wealth Sources - transform to ensure correct field mapping
      wealth_sources: transformWealthSources(updates.wealthSources),

      // Wealth Profile-Level Fields (Story 2.2.7)
      wealth_narrative: (updates.wealthSources as any)?.wealthNarrative || undefined,
      total_net_worth: (updates.wealthSources as any)?.totalNetWorth || undefined,
      liquid_net_worth: (updates.wealthSources as any)?.liquidNetWorth || undefined,
      net_worth_currency: (updates.wealthSources as any)?.netWorthCurrency || undefined,
      primary_wealth_source: (() => {
        const v = (updates.wealthSources as any)?.primaryWealthSource;
        return Array.isArray(v) ? v.join(', ') : v || undefined;
      })(),
      asset_breakdown_cash: (updates.wealthSources as any)?.assetBreakdown?.cash || undefined,
      asset_breakdown_securities: (updates.wealthSources as any)?.assetBreakdown?.securities || undefined,
      asset_breakdown_real_estate: (updates.wealthSources as any)?.assetBreakdown?.realEstate || undefined,
      asset_breakdown_business_equity: (updates.wealthSources as any)?.assetBreakdown?.businessEquity || undefined,
      asset_breakdown_commodities: (updates.wealthSources as any)?.assetBreakdown?.commodities || undefined,
      asset_breakdown_crypto: (updates.wealthSources as any)?.assetBreakdown?.crypto || undefined,
      asset_breakdown_other: (updates.wealthSources as any)?.assetBreakdown?.otherAssets || undefined,
      inheritance_expected: (updates.wealthSources as any)?.inheritanceExpected || undefined,
      inheritance_details: (updates.wealthSources as any)?.inheritanceDetails || undefined,
      unusual_transactions: (updates.wealthSources as any)?.unusualTransactions || undefined,
      unusual_transaction_details: (updates.wealthSources as any)?.unusualTransactionDetails || undefined,
      source_documentation: (updates.wealthSources as any)?.sourceDocumentation || undefined,

      // Income Expectations (Story 2.2.8) - Profile Level
      expected_income_change: (updates.incomeSources as any)?.expectedIncomeChange || undefined,
      expected_change_details: (updates.incomeSources as any)?.expectedChangeDetails || undefined,
      retirement_income: (updates.incomeSources as any)?.retirementIncome || undefined,
      retirement_income_details: (updates.incomeSources as any)?.retirementIncomeDetails || undefined,
      other_financial_support: (updates.incomeSources as any)?.otherFinancialSupport || undefined,
      other_support_details: (updates.incomeSources as any)?.otherSupportDetails || undefined,

      // Income Sources - extract ONLY the sources array, dedup by (incomeType + sourceName)
      income_sources: (() => {
        const raw = updates.incomeSources
          ? (Array.isArray(updates.incomeSources)
              ? updates.incomeSources
              : (updates.incomeSources as any)?.sources || [])
          : [];
        if (!raw.length) return undefined;
        const seen = new Set<string>();
        const ISO_COUNTRY = /^[A-Z]{2}$/;
        return raw.filter((s: any) => {
          const key = `${s.incomeType}||${s.sourceName}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }).map((s: any) => ({
          ...s,
          sourceCountry: s.sourceCountry && ISO_COUNTRY.test(s.sourceCountry) ? s.sourceCountry : undefined,
        }));
      })(),

      documents: updates.documents || undefined
    };

    // Clean empty strings
    const cleanedPayload = cleanEmptyStrings(payload);

    console.log('[KYC Service] Sending UPDATE KYC profile payload:', JSON.stringify(cleanedPayload, null, 2));

    const response = await apiClient.put(`/api/kyc/profiles/${clientId}`, cleanedPayload);
    return response.data;
  },

  /**
   * Delete KYC profile
   */
  delete: async (clientId: string) => {
    const response = await apiClient.delete(`/api/kyc/profiles/${clientId}`);
    return response.data;
  },

  /**
   * Update KYC profile status
   */
  updateStatus: async (clientId: string, status: string, notes?: string) => {
    const response = await apiClient.patch(`/api/kyc/profiles/${clientId}/status`, {
      status,
      notes
    });
    return response.data;
  },

  /**
   * List KYC profiles with filters
   */
  list: async (filters?: {
    status?: string;
    risk_level?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await apiClient.get('/api/kyc/profiles/', { params: filters });
    return response.data;
  }
};

/**
 * Document Operations
 */
export const kycDocumentAPI = {
  /**
   * Upload document
   */
  upload: async (file: File, kycProfileId: string, documentType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kyc_profile_id', kycProfileId);
    formData.append('document_type', documentType);

    const response = await apiClient.post('/api/kyc/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Extract data from document (OCR)
   */
  extract: async (documentId: string) => {
    const response = await apiClient.post('/api/kyc/documents/extract', {
      document_id: documentId
    });
    return response.data;
  },

  /**
   * Get document metadata
   */
  get: async (documentId: string) => {
    const response = await apiClient.get(`/api/kyc/documents/${documentId}`);
    return response.data;
  },

  /**
   * Delete document
   */
  delete: async (documentId: string) => {
    const response = await apiClient.delete(`/api/kyc/documents/${documentId}`);
    return response.data;
  },

  /**
   * List documents for KYC profile
   */
  list: async (kycProfileId: string) => {
    const response = await apiClient.get('/api/kyc/documents/', {
      params: { kyc_profile_id: kycProfileId }
    });
    return response.data;
  }
};

/**
 * Validation and Screening Operations
 */
export const kycValidationAPI = {
  /**
   * Check PEP status
   */
  checkPEP: async (profileData: {
    profile_id?: string;
    first_name: string;
    last_name: string;
    date_of_birth?: string;
  }) => {
    const response = await apiClient.post('/api/kyc/validation/check-pep', profileData);
    return response.data;
  },

  /**
   * Check sanctions lists
   */
  checkSanctions: async (profileData: {
    profile_id?: string;
    first_name: string;
    last_name: string;
    check_lists?: string[];
  }) => {
    const response = await apiClient.post('/api/kyc/validation/sanctions', profileData);
    return response.data;
  },

  /**
   * Check adverse media
   */
  checkAdverseMedia: async (profileData: {
    profile_id?: string;
    first_name: string;
    last_name: string;
    sources?: string[];
  }) => {
    const response = await apiClient.post('/api/kyc/validation/adverse-media', profileData);
    return response.data;
  },

  /**
   * Perform risk assessment
   */
  assessRisk: async (profileData: {
    profile_id?: string;
    nationalities?: string[];
    wealth_sources?: string[];
    is_pep?: boolean;
    is_sanctioned?: boolean;
    has_adverse_media?: boolean;
    include_screening?: boolean;
  }) => {
    const response = await apiClient.post('/api/kyc/validation/risk-assessment', profileData);
    return response.data;
  },

  /**
   * Get validation rules
   */
  getValidationRules: async () => {
    const response = await apiClient.get('/api/kyc/validation/rules');
    return response.data;
  }
};

/**
 * Workflow Operations
 */
export const kycWorkflowAPI = {
  /**
   * Submit for review
   */
  submitForReview: async (clientId: string, kycProfileId: string, notes?: string) => {
    const response = await apiClient.post(`/api/kyc/workflow/${clientId}/submit`, {
      kyc_profile_id: kycProfileId,
      notes
    });
    return response.data;
  },

  /**
   * Approve KYC profile
   */
  approve: async (clientId: string, kycProfileId: string, notes?: string) => {
    const response = await apiClient.post(`/api/kyc/workflow/${clientId}/approve`, {
      kyc_profile_id: kycProfileId,
      notes
    });
    return response.data;
  },

  /**
   * Reject KYC profile
   */
  reject: async (clientId: string, kycProfileId: string, rejectionReason: string) => {
    const response = await apiClient.post(`/api/kyc/workflow/${clientId}/reject`, {
      kyc_profile_id: kycProfileId,
      rejection_reason: rejectionReason
    });
    return response.data;
  },

  /**
   * Get pending reviews
   */
  getPendingReview: async (filters?: {
    priority?: string;
    assigned_to?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await apiClient.get('/api/kyc/workflow/pending-review', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get workflow statistics
   */
  getStatistics: async () => {
    const response = await apiClient.get('/api/kyc/workflow/statistics');
    return response.data;
  }
};

/**
 * Main KYC Service Export
 */
const kycService = {
  profiles: kycProfileAPI,
  documents: kycDocumentAPI,
  validation: kycValidationAPI,
  workflow: kycWorkflowAPI,

  /**
   * Get KYC prefill data from client's extracted documents (TASK-133)
   * Fetches AI-extracted "Draft KYC" data with confidence scores
   *
   * @param clientId - The client ID to fetch prefill data for
   * @returns Prefill data with confidence scores, or null if no data available
   */
  getPrefillData: async (clientId: string) => {
    try {
      const response = await apiClient.get(`/api/clients/${clientId}/kyc-prefill`);
      const prefillData = response.data;

      if (!prefillData || !prefillData.prefill_data) {
        return null;
      }

      // Helper to extract value from confidence-wrapped OR unwrapped fields
      // Format: {value: X, confidence: Y} -> returns {value: X, confidence: Y}
      // Format: X -> returns {value: X, confidence: 0}
      const extractField = (field: any) => {
        // Handle null/undefined
        if (field === null || field === undefined) {
          return null;
        }

        // Handle wrapped format: {value: X, confidence: Y}
        if (typeof field === 'object' && 'value' in field) {
          return {
            value: field.value,
            confidence: field.confidence || 0
          };
        }

        // Handle unwrapped format: X (direct value)
        // This handles cases where backend sends "gender": "male" instead of "gender": {"value": "male", "confidence": 0.92}
        return {
          value: field,
          confidence: 0  // No confidence score for unwrapped values
        };
      };

      const profileData = prefillData.prefill_data;

      // Transform backend structure to frontend KYCFormData format
      const transformed: Partial<KYCFormData> & {
        _confidence?: Record<string, number>;
        _extractionMetadata?: any;
      } = {
        clientId,
        _confidence: {}, // Store confidence scores separately for badge display
      };

      // Personal Info
      if (profileData.personalInfo) {
        const pi = profileData.personalInfo;
        transformed.personalInfo = {
          firstName: extractField(pi.firstName)?.value || '',
          middleName: extractField(pi.middleName)?.value || '',
          lastName: extractField(pi.lastName)?.value || '',
          dateOfBirth: extractField(pi.dateOfBirth)?.value || '',
          placeOfBirth: typeof pi.placeOfBirth === 'string' ? pi.placeOfBirth : (extractField(pi.placeOfBirth)?.value || ''),
          gender: extractField(pi.gender)?.value || '',
          maritalStatus: extractField(pi.maritalStatus)?.value || '',
          nationalities: extractField(pi.nationalities)?.value || [],
          formerNames: extractField(pi.formerNames)?.value || [],
          languages: extractField(pi.languages)?.value || []
        };

        // Store confidence scores for fields
        if (extractField(pi.firstName)) {
          transformed._confidence!['personalInfo.firstName'] = extractField(pi.firstName)!.confidence;
        }
        if (extractField(pi.middleName)) {
          transformed._confidence!['personalInfo.middleName'] = extractField(pi.middleName)!.confidence;
        }
        if (extractField(pi.lastName)) {
          transformed._confidence!['personalInfo.lastName'] = extractField(pi.lastName)!.confidence;
        }
        if (extractField(pi.dateOfBirth)) {
          transformed._confidence!['personalInfo.dateOfBirth'] = extractField(pi.dateOfBirth)!.confidence;
        }
        if (extractField(pi.placeOfBirth)) {
          transformed._confidence!['personalInfo.placeOfBirth'] = extractField(pi.placeOfBirth)!.confidence;
        }
        if (extractField(pi.gender)) {
          transformed._confidence!['personalInfo.gender'] = extractField(pi.gender)!.confidence;
        }
        if (extractField(pi.maritalStatus)) {
          transformed._confidence!['personalInfo.maritalStatus'] = extractField(pi.maritalStatus)!.confidence;
        }
        if (extractField(pi.nationalities)) {
          transformed._confidence!['personalInfo.nationalities'] = extractField(pi.nationalities)!.confidence;
        }
      }

      // Contact Info
      if (profileData.contactInfo) {
        const ci = profileData.contactInfo;

        // Extract residential address
        const residentialAddr = extractField(ci.residentialAddress)?.value;
        const mailingAddr = extractField(ci.mailingAddress)?.value;

        // Only set contactInfo if we have a valid residential address (required field)
        const validResidentialAddress = (residentialAddr && residentialAddr.street && residentialAddr.city && residentialAddr.postalCode && residentialAddr.country) ? {
          street: residentialAddr.street,
          apartment: residentialAddr.apartment || '',
          city: residentialAddr.city,
          state: residentialAddr.state || '',
          postalCode: residentialAddr.postalCode || residentialAddr.postal_code || '',
          country: residentialAddr.country
        } : null;

        if (validResidentialAddress) {
          transformed.contactInfo = {
            phonePrimary: extractField(ci.phonePrimary)?.value || '',
            phoneSecondary: extractField(ci.phoneSecondary)?.value || '',
            emailPrimary: extractField(ci.emailPrimary)?.value || '',
            emailSecondary: extractField(ci.emailSecondary)?.value || '',
            // Contact Preferences & Relationship (Story 2.2.3)
            preferredContactMethod: extractField(ci.preferredContactMethod)?.value || undefined,
            relationshipReason: extractField(ci.relationshipReason)?.value || '',
            relationshipPurpose: extractField(ci.relationshipPurpose)?.value || '',
            residentialAddress: validResidentialAddress,
            mailingAddress: mailingAddr ? {
              street: mailingAddr.street || '',
              apartment: mailingAddr.apartment || '',
              city: mailingAddr.city || '',
              state: mailingAddr.state || '',
              postalCode: mailingAddr.postalCode || mailingAddr.postal_code || '',
              country: mailingAddr.country || ''
            } : undefined,
            previousAddresses: extractField(ci.previousAddresses)?.value || []
          };
        }

        // Store confidence scores
        if (extractField(ci.phonePrimary)) {
          transformed._confidence!['contactInfo.phonePrimary'] = extractField(ci.phonePrimary)!.confidence;
        }
        if (extractField(ci.phoneSecondary)) {
          transformed._confidence!['contactInfo.phoneSecondary'] = extractField(ci.phoneSecondary)!.confidence;
        }
        if (extractField(ci.emailPrimary)) {
          transformed._confidence!['contactInfo.emailPrimary'] = extractField(ci.emailPrimary)!.confidence;
        }
        if (extractField(ci.emailSecondary)) {
          transformed._confidence!['contactInfo.emailSecondary'] = extractField(ci.emailSecondary)!.confidence;
        }
        if (extractField(ci.preferredContactMethod)) {
          transformed._confidence!['contactInfo.preferredContactMethod'] = extractField(ci.preferredContactMethod)!.confidence;
        }
        if (extractField(ci.relationshipReason)) {
          transformed._confidence!['contactInfo.relationshipReason'] = extractField(ci.relationshipReason)!.confidence;
        }
        if (extractField(ci.relationshipPurpose)) {
          transformed._confidence!['contactInfo.relationshipPurpose'] = extractField(ci.relationshipPurpose)!.confidence;
        }
        if (extractField(ci.residentialAddress)) {
          transformed._confidence!['contactInfo.residentialAddress'] = extractField(ci.residentialAddress)!.confidence;
        }
        if (extractField(ci.mailingAddress)) {
          transformed._confidence!['contactInfo.mailingAddress'] = extractField(ci.mailingAddress)!.confidence;
        }
        if (extractField(ci.previousAddresses)) {
          transformed._confidence!['contactInfo.previousAddresses'] = extractField(ci.previousAddresses)!.confidence;
        }
      }

      // Employment Info
      if (profileData.employmentInfo) {
        const ei = profileData.employmentInfo;

        // Map employment status: normalize to backend format (hyphenated)
        let employmentStatus = extractField(ei.employmentStatus)?.value || '';
        if (employmentStatus === 'active') employmentStatus = 'employed';
        if (employmentStatus === 'selfEmployed') employmentStatus = 'self-employed';

        // Extract occupation - use position if occupation is too long
        let occupation = extractField(ei.occupation)?.value || extractField(ei.position)?.value || '';
        // If occupation is a long paragraph, try to extract just the title
        if (occupation && occupation.length > 100) {
          // Try to extract first sentence or use position field
          occupation = extractField(ei.position)?.value || occupation.split('.')[0] || occupation.substring(0, 100);
        }

        // Parse yearsEmployed: "over 40" -> 40, "5 years" -> 5
        let yearsEmployed = extractField(ei.yearsEmployed)?.value;
        if (typeof yearsEmployed === 'string') {
          const match = yearsEmployed.match(/\d+/);
          yearsEmployed = match ? parseInt(match[0]) : undefined;
        }

        transformed.employmentInfo = {
          employmentStatus: employmentStatus as any,
          employerName: extractField(ei.employerName)?.value || '',
          occupation: occupation,
          employmentIndustry: extractField(ei.employmentIndustry)?.value || '',
          yearsEmployed: yearsEmployed,
          // Work address (NEW)
          workAddress: ei.workAddress ? {
            street: extractField(ei.workAddress.street || ei.workAddress.street_address)?.value || '',
            city: extractField(ei.workAddress.city)?.value || '',
            postalCode: extractField(ei.workAddress.postalCode || ei.workAddress.postal_code)?.value || '',
            country: extractField(ei.workAddress.country)?.value || ''
          } : undefined,
          // Income (NEW)
          annualIncome: extractField(ei.annualIncome || ei.annual_income)?.value,
          incomeCurrency: extractField(ei.incomeCurrency || ei.income_currency)?.value || 'CHF'
        };

        // Store confidence scores
        if (extractField(ei.employmentStatus)) {
          transformed._confidence!['employmentInfo.employmentStatus'] = extractField(ei.employmentStatus)!.confidence;
        }
        if (extractField(ei.employerName)) {
          transformed._confidence!['employmentInfo.employerName'] = extractField(ei.employerName)!.confidence;
        }
        if (extractField(ei.occupation || ei.position)) {
          transformed._confidence!['employmentInfo.occupation'] = extractField(ei.occupation || ei.position)!.confidence;
        }
        if (extractField(ei.employmentIndustry)) {
          transformed._confidence!['employmentInfo.employmentIndustry'] = extractField(ei.employmentIndustry)!.confidence;
        }
        if (extractField(ei.yearsEmployed)) {
          transformed._confidence!['employmentInfo.yearsEmployed'] = extractField(ei.yearsEmployed)!.confidence;
        }
      }

      // Tax Info
      if (profileData.taxInfo) {
        const ti = profileData.taxInfo;

        // Convert tax residence to ISO code (Switzerland -> CH, etc.)
        let taxResidence = extractField(ti.taxResidence)?.value || '';
        if (taxResidence && taxResidence.length > 2) {
          taxResidence = toISOCode(taxResidence);
        }

        // Map tax status to FATCA status if needed
        const fatcaStatus = extractField(ti.fatcaStatus || ti.taxStatus)?.value || '';

        transformed.taxInfo = {
          taxId: extractField(ti.taxId)?.value || '',
          taxResidence: taxResidence,
          usPerson: extractField(ti.usPerson)?.value || false,
          fatcaStatus: fatcaStatus as any
        };

        // Store confidence scores
        if (extractField(ti.taxId)) {
          transformed._confidence!['taxInfo.taxId'] = extractField(ti.taxId)!.confidence;
        }
        if (extractField(ti.taxResidence)) {
          transformed._confidence!['taxInfo.taxResidence'] = extractField(ti.taxResidence)!.confidence;
        }
        if (extractField(ti.usPerson)) {
          transformed._confidence!['taxInfo.usPerson'] = extractField(ti.usPerson)!.confidence;
        }
        if (extractField(ti.fatcaStatus || ti.taxStatus)) {
          transformed._confidence!['taxInfo.fatcaStatus'] = extractField(ti.fatcaStatus || ti.taxStatus)!.confidence;
        }
      }

      // Wealth Sources Array (individual wealth sources)
      if (profileData.wealthSources && Array.isArray(profileData.wealthSources)) {
        // Filter out empty objects and map to frontend format
        const validSources = profileData.wealthSources.filter((source: any) =>
          source && Object.keys(source).length > 0
        );

        console.log('[TASK-133] Wealth sources transformation:', {
          original: profileData.wealthSources,
          filtered: validSources,
          count: validSources.length
        });

        transformed.wealthSources = validSources.map((source: any, index: number) => {
          // Backend uses assetType, assetValue, assetCurrency, sourceType, sourceDescription
          const wealthSource = {
            sourceType: extractField(source.sourceType)?.value || '',
            sourceDescription: extractField(source.sourceDescription)?.value || '',
            percentage: extractField(source.percentage)?.value || 0,
            assetType: extractField(source.assetType)?.value || '',
            assetLocation: extractField(source.assetLocation)?.value || '',
            assetValue: extractField(source.assetValue)?.value,
            assetCurrency: extractField(source.assetCurrency)?.value || 'CHF',
            documented: extractField(source.documentationAvailable || source.documented)?.value,
            // Date fields (Story 2.2.7)
            acquisitionPeriod: extractField(source.acquisitionPeriod)?.value || '',
            acquisitionStartDate: extractField(source.acquisitionStartDate)?.value || undefined,
            acquisitionEndDate: extractField(source.acquisitionEndDate)?.value || undefined,
            isOngoing: extractField(source.isOngoing)?.value ?? false,
            // Liquidity
            liquidityLevel: extractField(source.liquidityLevel)?.value || undefined,
            // Document support
            supportingDocumentTypes: extractField(source.supportingDocumentTypes)?.value || [],
            uploadedDocumentIds: extractField(source.uploadedDocumentIds)?.value || [],
            // Verification
            verificationNotes: extractField(source.verificationNotes)?.value || ''
          };

          // Store confidence for array items
          const sourceTypeField = source.sourceType || source.assetType;
          if (extractField(sourceTypeField)) {
            transformed._confidence![`wealthSources[${index}].sourceType`] = extractField(sourceTypeField)!.confidence;
          }
          if (extractField(source.assetValue)) {
            transformed._confidence![`wealthSources[${index}].assetValue`] = extractField(source.assetValue)!.confidence;
          }

          console.log(`[TASK-133] Wealth source #${index + 1}:`, wealthSource);
          return wealthSource;
        });
      }

      // Wealth Summary - Profile Level Fields (Story 2.2.7)
      // These are NOT per-source fields - they apply to the entire profile
      console.log('[TASK-133] Extracting profile-level wealth summary fields:', {
        totalNetWorth: profileData.totalNetWorth,
        liquidNetWorth: profileData.liquidNetWorth,
        assetBreakdownCash: profileData.assetBreakdownCash,
        wealthNarrative: profileData.wealthNarrative ? 'present' : 'missing'
      });

      // Net Worth Overview
      if (profileData.totalNetWorth !== undefined) {
        transformed.totalNetWorth = extractField(profileData.totalNetWorth)?.value;
      }
      if (profileData.liquidNetWorth !== undefined) {
        transformed.liquidNetWorth = extractField(profileData.liquidNetWorth)?.value;
      }
      if (profileData.netWorthCurrency !== undefined) {
        transformed.netWorthCurrency = extractField(profileData.netWorthCurrency)?.value || 'CHF';
      }
      if (profileData.primaryWealthSource !== undefined) {
        transformed.primaryWealthSource = extractField(profileData.primaryWealthSource)?.value;
      }

      // Asset Breakdown Percentages (should sum to 100%)
      if (profileData.assetBreakdownCash !== undefined) {
        transformed.assetBreakdownCash = extractField(profileData.assetBreakdownCash)?.value;
      }
      if (profileData.assetBreakdownSecurities !== undefined) {
        transformed.assetBreakdownSecurities = extractField(profileData.assetBreakdownSecurities)?.value;
      }
      if (profileData.assetBreakdownRealEstate !== undefined) {
        transformed.assetBreakdownRealEstate = extractField(profileData.assetBreakdownRealEstate)?.value;
      }
      if (profileData.assetBreakdownBusinessEquity !== undefined) {
        transformed.assetBreakdownBusinessEquity = extractField(profileData.assetBreakdownBusinessEquity)?.value;
      }
      if (profileData.assetBreakdownCommodities !== undefined) {
        transformed.assetBreakdownCommodities = extractField(profileData.assetBreakdownCommodities)?.value;
      }
      if (profileData.assetBreakdownCrypto !== undefined) {
        transformed.assetBreakdownCrypto = extractField(profileData.assetBreakdownCrypto)?.value;
      }
      if (profileData.assetBreakdownOther !== undefined) {
        transformed.assetBreakdownOther = extractField(profileData.assetBreakdownOther)?.value;
      }

      // Additional Wealth Information
      if (profileData.inheritanceExpected !== undefined) {
        transformed.inheritanceExpected = extractField(profileData.inheritanceExpected)?.value;
      }
      if (profileData.inheritanceDetails !== undefined) {
        transformed.inheritanceDetails = extractField(profileData.inheritanceDetails)?.value;
      }
      if (profileData.unusualTransactions !== undefined) {
        transformed.unusualTransactions = extractField(profileData.unusualTransactions)?.value;
      }
      if (profileData.unusualTransactionDetails !== undefined) {
        transformed.unusualTransactionDetails = extractField(profileData.unusualTransactionDetails)?.value;
      }
      if (profileData.sourceDocumentation !== undefined) {
        transformed.sourceDocumentation = extractField(profileData.sourceDocumentation)?.value;
      }

      // Wealth Narrative
      if (profileData.wealthNarrative !== undefined) {
        transformed.wealthNarrative = extractField(profileData.wealthNarrative)?.value;
      }

      console.log('[TASK-133] Profile-level wealth summary extracted:', {
        totalNetWorth: transformed.totalNetWorth,
        liquidNetWorth: transformed.liquidNetWorth,
        assetBreakdownCash: transformed.assetBreakdownCash,
        assetBreakdownSecurities: transformed.assetBreakdownSecurities,
        wealthNarrative: transformed.wealthNarrative ? 'present' : 'missing'
      });

      // Income Sources (Story 2.2.8) - Handle both old array format and new object format
      if (profileData.incomeSources) {
        // Check if it's the new object format with top-level fields + sources array
        if (typeof profileData.incomeSources === 'object' && !Array.isArray(profileData.incomeSources)) {
          console.log('[TASK-133] Income sources (object format):', profileData.incomeSources);

          // Transform the sources array from snake_case to camelCase
          const transformedSources = profileData.incomeSources.sources?.map((source: any) => ({
            incomeType: source.income_type || source.incomeType || '',
            incomeDescription: source.income_description || source.incomeDescription || '',
            annualAmount: source.annual_amount || source.annualAmount || 0,
            currency: source.currency || 'CHF',
            frequency: source.frequency,
            sourceName: source.source_name || source.sourceName || '',
            sourceCountry: source.source_country || source.sourceCountry,
            startDate: source.start_date || source.startDate,
            isOngoing: source.is_ongoing ?? source.isOngoing ?? true,
            verified: source.verified ?? false,
            taxPaid: source.tax_paid ?? source.taxPaid ?? true,
            taxJurisdiction: source.tax_jurisdiction || source.taxJurisdication,
            taxRate: source.tax_rate || source.taxRate,
            stabilityRating: source.stability_rating || source.stabilityRating,
            expectedContinuity: source.expected_continuity || source.expectedContinuity,
            supportingDocuments: Array.isArray(source.supporting_documents)
              ? source.supporting_documents.join(', ')
              : source.supportingDocuments || ''
          })) || [];

          // Use the object structure with transformed sources
          transformed.incomeSources = {
            ...profileData.incomeSources,
            sources: transformedSources
          };

          console.log('[TASK-133] Transformed income sources:', transformed.incomeSources);
        }
        // Legacy: Handle old array format
        else if (Array.isArray(profileData.incomeSources)) {
          // Filter out empty objects and map to frontend format
          const validSources = profileData.incomeSources.filter((source: any) =>
            source && Object.keys(source).length > 0
          );

          console.log('[TASK-133] Income sources transformation (legacy array):', {
            original: profileData.incomeSources,
            filtered: validSources,
            count: validSources.length
          });

          transformed.incomeSources = validSources.map((source: any, index: number) => {
            // Backend returns snake_case, need to handle both formats
            const incomeSource = {
              incomeType: extractField(source.incomeType || source.income_type)?.value || '',
              incomeDescription: extractField(source.incomeDescription || source.income_description)?.value || '',
              annualAmount: extractField(source.annualAmount || source.annual_amount)?.value,
              currency: extractField(source.currency)?.value || 'CHF',
              frequency: extractField(source.frequency)?.value || undefined,
              sourceName: extractField(source.sourceName || source.source_name)?.value || '',
              sourceCountry: extractField(source.sourceCountry || source.source_country)?.value || '',
              startDate: extractField(source.startDate || source.start_date)?.value || undefined,
              isOngoing: extractField(source.isOngoing || source.is_ongoing)?.value ?? true,
              verified: extractField(source.verified)?.value ?? false,
              taxPaid: extractField(source.taxPaid || source.tax_paid)?.value ?? true,
              taxJurisdiction: extractField(source.taxJurisdiction || source.tax_jurisdiction)?.value || undefined,
              taxRate: extractField(source.taxRate || source.tax_rate)?.value || undefined,
              stabilityRating: extractField(source.stabilityRating || source.stability_rating)?.value || undefined,
              expectedContinuity: extractField(source.expectedContinuity || source.expected_continuity)?.value || undefined,
              // API returns array but form expects string, join with commas
              supportingDocuments: Array.isArray(source.supporting_documents || source.supportingDocuments)
                ? (source.supporting_documents || source.supportingDocuments).join(', ')
                : extractField(source.supportingDocuments || source.supporting_documents)?.value || ''
            };

            // Store confidence for array items
            if (extractField(source.incomeType || source.income_type)) {
              transformed._confidence![`incomeSources[${index}].incomeType`] = extractField(source.incomeType || source.income_type)!.confidence;
            }
            if (extractField(source.annualAmount || source.annual_amount)) {
              transformed._confidence![`incomeSources[${index}].annualAmount`] = extractField(source.annualAmount || source.annual_amount)!.confidence;
            }

            console.log(`[TASK-133] Income source #${index + 1}:`, incomeSource);
            return incomeSource;
          });
        }
      }

      // Business Activities
      if (profileData.businessActivities && Array.isArray(profileData.businessActivities)) {
        // Filter out empty objects and map to frontend format
        const validActivities = profileData.businessActivities.filter((activity: any) =>
          activity && Object.keys(activity).length > 0
        );

        console.log('[TASK-133] Business activities transformation:', {
          original: profileData.businessActivities,
          filtered: validActivities,
          count: validActivities.length
        });

        transformed.businessActivities = validActivities.map((activity: any, index: number) => {
          const businessActivity = {
            businessName: extractField(activity.businessName)?.value || '',
            businessType: extractField(activity.businessType)?.value || 'other',
            registrationNumber: extractField(activity.registrationNumber)?.value || '',
            registrationCountry: extractField(activity.registrationCountry)?.value || '',
            primaryIndustry: extractField(activity.primaryIndustry)?.value || '',
            secondaryIndustries: extractField(activity.secondaryIndustries)?.value || [],
            industryRiskLevel: extractField(activity.industryRiskLevel)?.value || 'low',
            naicsCode: extractField(activity.naicsCode)?.value || '',
            ownershipPercentage: extractField(activity.ownershipPercentage)?.value,
            ownershipType: extractField(activity.ownershipType)?.value || 'direct',
            otherOwners: extractField(activity.otherOwners)?.value || [],
            countriesOfOperation: extractField(activity.countriesOfOperation)?.value || [],
            highRiskCountries: extractField(activity.highRiskCountries)?.value || [],
            yearsInBusiness: extractField(activity.yearsInBusiness)?.value,
            numberOfEmployees: extractField(activity.numberOfEmployees)?.value,
            annualRevenue: extractField(activity.annualRevenue)?.value,
            revenueCurrency: extractField(activity.revenueCurrency)?.value || 'CHF',
            cashIntensive: extractField(activity.cashIntensive)?.value || false,
            crossBorderTransactions: extractField(activity.crossBorderTransactions)?.value || false,
            complexOwnership: extractField(activity.complexOwnership)?.value || false
          };

          // Store confidence for array items
          if (extractField(activity.businessName)) {
            transformed._confidence![`businessActivities[${index}].businessName`] = extractField(activity.businessName)!.confidence;
          }
          if (extractField(activity.businessType)) {
            transformed._confidence![`businessActivities[${index}].businessType`] = extractField(activity.businessType)!.confidence;
          }
          if (extractField(activity.primaryIndustry)) {
            transformed._confidence![`businessActivities[${index}].primaryIndustry`] = extractField(activity.primaryIndustry)!.confidence;
          }

          console.log(`[TASK-133] Business activity #${index + 1}:`, businessActivity);
          return businessActivity;
        });
      }

      // Personal Background (Story 2.2.3)
      if (profileData.personalBackground) {
        const pb = profileData.personalBackground;

        transformed.personalBackground = {
          educationLevel: extractField(pb.education_level || pb.educationLevel)?.value || '',
          institutionsAttended: extractField(pb.institutions_attended || pb.institutionsAttended)?.value || [],
          professionalCertifications: extractField(pb.professional_certifications || pb.professionalCertifications)?.value || [],
          hobbiesInterests: extractField(pb.hobbies_interests || pb.hobbiesInterests)?.value || [],
          familyComposition: extractField(pb.family_composition || pb.familyComposition)?.value || '',
          dependentsCount: extractField(pb.dependents_count || pb.dependentsCount)?.value,
          spousePartnerEmploymentStatus: extractField(pb.spouse_partner_employment_status || pb.spousePartnerEmploymentStatus)?.value || ''
        };

        console.log('[TASK-133] Personal background transformation:', transformed.personalBackground);
      }

      // PEP Status (keep for backward compatibility, but not used by AdverseMediaStep)
      if (profileData.pepStatus) {
        const ps = profileData.pepStatus;

        transformed.pepStatus = {
          // Classification
          classification: extractField(ps.classification)?.value || '',
          isPEP: extractField(ps.is_pep || ps.isPEP)?.value || false,
          isRCA: extractField(ps.is_rca || ps.isRCA)?.value || false,

          // PEP Details
          pepType: extractField(ps.pep_type || ps.pepType)?.value || '',
          positions: extractField(ps.positions)?.value || [],
          countries: extractField(ps.countries)?.value || [],
          boardPositionIsins: extractField(ps.board_position_isins || ps.boardPositionIsins)?.value || [],

          // Dates and Duration
          startDate: extractField(ps.start_date || ps.startDate)?.value || '',
          endDate: extractField(ps.end_date || ps.endDate)?.value || '',
          yearsInPosition: extractField(ps.years_in_position || ps.yearsInPosition)?.value,

          // Details field can store additional information
          details: extractField(ps.details)?.value || ''
        };

        console.log('[TASK-133] PEP status transformation:', transformed.pepStatus);
      }

      // Adverse Media
      // Backend already transforms pepStatus to adverseMedia format, so use it directly
      if (profileData.adverseMedia) {
        // Check if it's already transformed to form format (object with isPEP, etc)
        if (typeof profileData.adverseMedia === 'object' && !Array.isArray(profileData.adverseMedia)) {
          // Backend sent transformed format - use directly
          transformed.adverseMedia = profileData.adverseMedia;
          console.log('[TASK-133] Using pre-transformed adverse media from backend:', profileData.adverseMedia);
        } else if (Array.isArray(profileData.adverseMedia)) {
          // Old array format - transform individual items
          const validMedia = profileData.adverseMedia.filter((media: any) =>
            media && Object.keys(media).length > 0
          );

          transformed.adverseMedia = validMedia.map((media: any) => ({
            hasAdverseMedia: extractField(media.has_adverse_media || media.hasAdverseMedia)?.value || false,
            mediaType: extractField(media.media_type || media.mediaType)?.value || '',
            severityLevel: extractField(media.severity_level || media.severityLevel)?.value || '',
            description: extractField(media.description)?.value || '',
            source: extractField(media.source)?.value || '',
            date: extractField(media.date)?.value || '',
            verified: extractField(media.verified)?.value || false
          })) as any;

          console.log('[TASK-133] Adverse media array transformation:', {
            count: validMedia.length,
            items: transformed.adverseMedia
          });
        }
      }

      // Add extraction metadata
      if (prefillData.extraction_metadata) {
        transformed._extractionMetadata = {
          lastExtracted: prefillData.extraction_metadata.last_extracted,
          sourceDocuments: prefillData.extraction_metadata.source_documents || [],
          overallConfidence: prefillData.extraction_metadata.overall_confidence
        };
      }

      console.log('KYC prefill data loaded:', {
        clientId,
        fieldsCount: Object.keys(transformed._confidence || {}).length,
        overallConfidence: prefillData.overall_confidence
      });

      return transformed;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No prefill data available - this is normal for new clients
        console.log('No prefill data available for client:', clientId);
        return null;
      }
      console.error('Failed to fetch KYC prefill data:', error);
      throw error;
    }
  },

  /**
   * Save draft to backend (full save)
   */
  saveDraft: async (formData: Partial<KYCFormData>) => {
    const { clientId } = formData;
    if (!clientId) throw new Error('Client ID is required');

    try {
      // Check if profile exists
      const existing = await kycProfileAPI.get(clientId).catch(() => null);

      if (existing) {
        // Update existing profile
        return await kycProfileAPI.update(clientId, formData);
      } else {
        // Create new profile
        return await kycProfileAPI.create(formData);
      }
    } catch (error) {
      console.error('Failed to save KYC draft:', error);
      throw error;
    }
  },

  /**
   * Load draft from backend
   */
  loadDraft: async (clientId: string) => {
    let profile = null;

    try {
      profile = await kycProfileAPI.get(clientId);
    } catch (error: any) {
      // 404 is expected for new clients - don't throw
      if (error.response?.status === 404) {
        console.log('No existing KYC profile for client, trying seed data:', clientId);
      } else {
        console.log('API KYC fetch failed for draft, trying seed data:', error);
      }

      // TASK-210: Try seed data if API fails
      const { getClientKYC } = await import('./mock/comprehensiveClientSeedData');
      const seedKYC = getClientKYC(clientId);
      if (seedKYC) {
        profile = seedKYC;
        console.log('Loaded KYC draft from seed data:', clientId);
      }
      // For new clients without seed data, profile remains null (no error)
    }

    if (profile) {
      // Transform backend data to frontend format
      return {
        clientId: profile.client_id,
        version: profile.version,
        status: profile.status,
        personalInfo: {
          firstName: profile.first_name,
          middleName: profile.middle_name,  // Fixed: was middle_names (plural)
          lastName: profile.last_name,
          passportNumber: profile.passport_number,
          nationalId: profile.national_id,
          ssn: profile.ssn,
          dateOfBirth: profile.date_of_birth,
          placeOfBirth: profile.place_of_birth,
          gender: profile.gender,
          maritalStatus: profile.marital_status,
          nationalities: profile.nationalities || [],
          formerNames: profile.former_names || [],
          languages: profile.languages || []
        },
        contactInfo: {
          phonePrimary: profile.phone_primary,
          phoneSecondary: profile.phone_secondary,
          emailPrimary: profile.email_primary,
          emailSecondary: profile.email_secondary,
          preferredContactMethod: profile.preferred_contact_method,
          relationshipReason: profile.relationship_reason,
          relationshipPurpose: profile.relationship_purpose,
          residentialAddress: profile.residential_address,
          mailingAddress: profile.mailing_address,
          previousAddresses: profile.previous_addresses || []
        },
        employmentInfo: {
          employmentStatus: profile.employment_status,
          employerName: profile.employer_name,
          occupation: profile.occupation,
          employmentIndustry: profile.employment_industry,
          yearsEmployed: profile.years_employed,
          // Work address (NEW)
          workAddress: profile.work_address ? {
            street: profile.work_address.street_address || '',
            city: profile.work_address.city || '',
            postalCode: profile.work_address.postal_code || '',
            country: profile.work_address.country || ''
          } : undefined,
          // Income (NEW)
          annualIncome: profile.annual_income,
          incomeCurrency: profile.income_currency || 'CHF'
        },
        taxInfo: {
          taxId: profile.tax_id,
          taxResidence: profile.tax_residence,
          usPerson: profile.us_person,
          fatcaStatus: profile.fatca_status
        },
        // Transform snake_case to camelCase for personalBackground
        personalBackground: profile.personal_background ? {
          educationLevel: profile.personal_background.education_level || '',
          institutionsAttended: profile.personal_background.institutions_attended || [],
          professionalCertifications: profile.personal_background.professional_certifications || [],
          hobbiesInterests: profile.personal_background.hobbies_interests || [],
          familyComposition: profile.personal_background.family_composition || '',
          dependentsCount: profile.personal_background.dependents_count,
          spousePartnerEmploymentStatus: profile.personal_background.spouse_partner_employment_status || ''
        } : undefined,
        // Transform snake_case to camelCase for pepStatus
        pepStatus: profile.pep_status ? {
          classification: profile.pep_status.classification || '',
          isPEP: profile.pep_status.is_pep || false,
          isRCA: profile.pep_status.is_rca || false,
          pepType: profile.pep_status.pep_type || '',
          positions: profile.pep_status.positions || [],
          countries: profile.pep_status.countries || [],
          boardPositionIsins: profile.pep_status.board_position_isins || [],
          startDate: profile.pep_status.start_date || '',
          endDate: profile.pep_status.end_date || '',
          yearsInPosition: profile.pep_status.years_in_position,
          yearsSinceExit: profile.pep_status.years_since_exit,
          relatedPeps: profile.pep_status.related_peps || [],
          relationshipType: profile.pep_status.relationship_type || '',
          pepRiskLevel: profile.pep_status.pep_risk_level || '',
          enhancedDueDiligence: profile.pep_status.enhanced_due_diligence || false,
          monitoringFrequency: profile.pep_status.monitoring_frequency || ''
        } : undefined,
        // Transform snake_case to camelCase for adverseMedia
        adverseMedia: (profile.adverse_media || []).map((media: any) => ({
          hasAdverseMedia: media.has_adverse_media || false,
          mediaType: media.media_type || '',
          severityLevel: media.severity_level || '',
          description: media.description || '',
          source: media.source || '',
          date: media.date || '',
          verified: media.verified || false
        })),
        // Transform snake_case to camelCase for businessActivities
        businessActivities: (profile.business_activities || []).map((ba: any) => ({
          id: ba.id,
          businessName: ba.business_name,
          businessType: ba.business_type,
          registrationNumber: ba.registration_number,
          registrationCountry: ba.registration_country,
          primaryIndustry: ba.primary_industry,
          secondaryIndustries: ba.secondary_industries || [],
          industryRiskLevel: ba.industry_risk_level,
          naicsCode: ba.naics_code,
          ownershipPercentage: ba.ownership_percentage,
          ownershipType: ba.ownership_type,
          otherOwners: ba.other_owners || [],
          countriesOfOperation: ba.countries_of_operation || [],
          highRiskCountries: ba.high_risk_countries || [],
          yearsInBusiness: ba.years_in_business,
          numberOfEmployees: ba.number_of_employees,
          annualRevenue: ba.annual_revenue,
          revenueCurrency: ba.revenue_currency || 'CHF',
          cashIntensive: ba.cash_intensive || false,
          crossBorderTransactions: ba.cross_border_transactions || false,
          complexOwnership: ba.complex_ownership || false
        })),
        // Transform snake_case to camelCase for wealthSources
        wealthSources: (profile.wealth_sources || []).map((ws: any) => ({
          id: ws.id,
          sourceType: ws.source_type,
          sourceDescription: ws.source_description,
          percentage: ws.percentage,
          assetValue: ws.asset_value,
          assetCurrency: ws.asset_currency || 'CHF',
          assetType: ws.asset_type,
          assetLocation: ws.asset_location,
          acquisitionPeriod: ws.acquisition_period,
          acquisitionStartDate: ws.acquisition_start_date,
          acquisitionEndDate: ws.acquisition_end_date,
          isOngoing: ws.is_ongoing || false,
          yearsAccumulated: ws.years_accumulated,
          documented: ws.documented || false,
          documentationType: ws.documentation_type,
          verificationNotes: ws.verification_notes,
          liquidityLevel: ws.liquidity_level,
          liquidationTimeframe: ws.liquidation_timeframe,
          sourceRiskLevel: ws.source_risk_level,
          requiresEnhancedVerification: ws.requires_enhanced_verification || false,
          supportingDocumentTypes: ws.supporting_document_types || [],
          uploadedDocumentIds: ws.uploaded_document_ids || []
        })),
        // Transform snake_case to camelCase for incomeSources
        incomeSources: (profile.income_sources || []).map((is: any) => ({
          id: is.id,
          incomeType: is.income_type,
          incomeDescription: is.income_description,
          annualAmount: is.annual_amount,
          currency: is.currency || 'CHF',
          frequency: is.frequency,
          sourceName: is.source_name,
          sourceCountry: is.source_country,
          startDate: is.start_date,
          isOngoing: is.is_ongoing !== false,
          verified: is.verified || false,
          taxPaid: is.tax_paid !== false,
          taxJurisdiction: is.tax_jurisdiction,
          taxRate: is.tax_rate,
          stabilityRating: is.stability_rating,
          expectedContinuity: is.expected_continuity,
          supportingDocuments: is.supporting_documents
        })),
        documents: profile.documents || [],
        // Risk assessment
        riskAssessment: {
          riskLevel: profile.risk_level,
          riskScore: profile.risk_score,
          riskFactors: profile.risk_factors || [],
          requiresEnhancedDueDiligence: profile.risk_level === 'high' || profile.risk_level === 'critical'
        }
      } as Partial<KYCFormData>;
    }

    // If no profile found, return null
    return null;
  },

  /**
   * Submit KYC for review
   */
  submitForReview: async (formData: KYCFormData) => {
    const { clientId } = formData;
    if (!clientId) throw new Error('Client ID is required');

    try {
      // First save the complete form
      const savedProfile = await kycService.saveDraft(formData);

      // Then submit for review
      return await kycWorkflowAPI.submitForReview(
        clientId,
        savedProfile.profile_id || savedProfile.id,
        'Submitted via KYC wizard'
      );
    } catch (error) {
      console.error('Failed to submit KYC for review:', error);
      throw error;
    }
  },

  /**
   * Download KYC profile as PDF (Story 2.2.9)
   * Exports complete KYC data in PDF format for offline review
   */
  downloadKYCPDF: async (clientId: string): Promise<void> => {
    try {
      const response = await apiClient.get(
        `/api/kyc/profiles/${clientId}/export/pdf`,
        { responseType: 'blob' }
      );

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `KYC-${clientId}-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('KYC PDF downloaded successfully for client:', clientId);
    } catch (error: any) {
      console.error('PDF download failed:', error);

      // Provide helpful error message based on status
      if (error.response?.status === 404) {
        throw new Error('PDF export endpoint not available. Please contact support.');
      } else if (error.response?.status === 500) {
        throw new Error('Server error generating PDF. Please try again later.');
      } else {
        throw new Error('Failed to download PDF. Please check your connection.');
      }
    }
  },

  // ============================================================================
  // TASK-202: KYC Versioning Methods
  // ============================================================================

  /**
   * Get version history for a KYC profile
   *
   * @param clientId - Client ID
   * @param limit - Maximum number of versions to return (default: 50)
   * @param offset - Number of versions to skip for pagination (default: 0)
   * @returns Version history response with list of versions
   */
  getVersionHistory: async (
    clientId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<import('../types/kyc.types').VersionHistoryResponse> => {
    try {
      const response = await apiClient.get(
        `/api/kyc/profiles/${clientId}/versions`,
        { params: { limit, offset } }
      );

      console.log(`Loaded ${response.data.versions.length} versions for client ${clientId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to load version history:', error);

      if (error.response?.status === 404) {
        throw new Error('KYC profile not found');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied to version history');
      } else {
        throw new Error('Failed to load version history. Please try again.');
      }
    }
  },

  /**
   * Get complete snapshot of a specific version
   *
   * @param versionId - Version ID
   * @returns Version snapshot with complete KYC data
   */
  getVersionSnapshot: async (
    versionId: string
  ): Promise<import('../types/kyc.types').KYCVersionSnapshot> => {
    try {
      const response = await apiClient.get(`/api/kyc/profiles/versions/${versionId}`);

      console.log(`Loaded version snapshot ${versionId}`);
      return response.data.version;
    } catch (error: any) {
      console.error('Failed to load version snapshot:', error);

      if (error.response?.status === 404) {
        throw new Error('Version not found');
      } else {
        throw new Error('Failed to load version snapshot. Please try again.');
      }
    }
  },

  /**
   * Get diff for a specific version (compared to previous version)
   *
   * @param versionId - Version ID
   * @returns Diff showing what changed from previous version
   */
  getVersionDiff: async (
    versionId: string
  ): Promise<{
    success: boolean;
    diff: import('../types/kyc.types').VersionDiff;
    changedFields: string[];
    versionNumber: number;
    changeCategories: { added: number; modified: number; removed: number };
  }> => {
    try {
      const response = await apiClient.get(`/api/kyc/profiles/versions/${versionId}/diff`);

      console.log(`Loaded diff for version ${versionId}:`, {
        added: response.data.changeCategories.added,
        modified: response.data.changeCategories.modified,
        removed: response.data.changeCategories.removed
      });

      return response.data;
    } catch (error: any) {
      console.error('Failed to load version diff:', error);

      if (error.response?.status === 404) {
        throw new Error('Version not found');
      } else {
        throw new Error('Failed to load version diff. Please try again.');
      }
    }
  },

  /**
   * Compare two versions
   *
   * @param versionId1 - First version ID
   * @param versionId2 - Second version ID
   * @returns Diff between the two versions
   */
  compareVersions: async (
    versionId1: string,
    versionId2: string
  ): Promise<import('../types/kyc.types').VersionComparisonResponse> => {
    try {
      const response = await apiClient.post('/api/kyc/profiles/versions/compare', {
        versionId1,
        versionId2
      });

      console.log(`Compared versions ${versionId1} and ${versionId2}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to compare versions:', error);

      if (error.response?.status === 400) {
        throw new Error('Invalid version IDs or versions from different profiles');
      } else if (error.response?.status === 404) {
        throw new Error('One or both versions not found');
      } else {
        throw new Error('Failed to compare versions. Please try again.');
      }
    }
  },

  // ============================================================================
  // TASK-376: Client Profile & Form A Generation
  // ============================================================================

  /**
   * Generate Client Profile from approved KYC data
   */
  async generateClientProfile(clientId: string) {
    try {
      const response = await apiClient.post(`/api/kyc/${clientId}/generate-client-profile`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to generate client profile:', error);
      throw error;
    }
  },

  /**
   * Save Client Profile data
   */
  async saveClientProfile(clientId: string, data: Record<string, any>) {
    try {
      const response = await apiClient.put(`/api/kyc/${clientId}/client-profile`, data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to save client profile:', error);
      throw error;
    }
  },

  /**
   * Get saved Client Profile
   */
  async getClientProfile(clientId: string) {
    try {
      const response = await apiClient.get(`/api/kyc/${clientId}/client-profile`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get client profile:', error);
      throw error;
    }
  },

  /**
   * Generate Form A from approved KYC data
   */
  async generateFormA(clientId: string) {
    try {
      const response = await apiClient.post(`/api/kyc/${clientId}/generate-form-a`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to generate Form A:', error);
      throw error;
    }
  },

  /**
   * Save Form A data
   */
  async saveFormA(clientId: string, data: Record<string, any>) {
    try {
      const response = await apiClient.put(`/api/kyc/${clientId}/form-a`, data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to save Form A:', error);
      throw error;
    }
  },

  /**
   * Get saved Form A
   */
  async getFormA(clientId: string) {
    try {
      const response = await apiClient.get(`/api/kyc/${clientId}/form-a`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get Form A:', error);
      throw error;
    }
  },

  /**
   * Prefill risk profile sections 1 + 3 from approved KYC (for TASK-377)
   */
  async prefillRiskProfile(clientId: string) {
    try {
      const response = await apiClient.post(`/api/kyc/${clientId}/prefill-risk-profile`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to prefill risk profile:', error);
      throw error;
    }
  },

  /**
   * TASK-926: the client's STORED risk assessment. 404 means the client has no active KYC
   * profile — which the caller must render as an absence, never as a score, because the
   * columns default to 50/medium and a default is not an assessment of anybody.
   */
  async getRiskAssessment(clientId: string): Promise<StoredRiskAssessment> {
    const response = await apiClient.get(
      `/api/kyc/validation/clients/${clientId}/risk-assessment`,
    );
    return response.data;
  },

  /**
   * Re-score the client from their stored KYC profile and persist the result. Returns what
   * was written, so the tab renders the stored value rather than a local echo of the request.
   */
  async recalculateRiskAssessment(clientId: string): Promise<StoredRiskAssessment> {
    const response = await apiClient.post(
      `/api/kyc/validation/clients/${clientId}/risk-assessment`,
    );
    return response.data;
  },
};

/**
 * TASK-926: exactly what the server STORES — no weights, no history, because it has neither.
 * Named `Stored…` because `types/kyc.types.ts` already exports an unrelated `RiskAssessment`;
 * two same-named shapes one import apart is a collision waiting for the first careless import.
 */
export interface StoredRiskAssessment {
  client_id: string;
  kyc_profile_id: string;
  risk_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  risk_factors: Record<string, unknown> & {
    contributing_factors?: string[];
    recommended_actions?: string[];
  };
  next_review_date: string | null;
  last_review_date: string | null;
}

export default kycService;
